import { memo } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { DeviceAccount } from "../modal/deviceAccounts";
import DeviceAccountList from "../modal/DeviceAccountList";
import { CheckIcon, LockIcon, ReceiptIcon, WarningIcon } from "../modal/deviceModalIcons";
import {
  EYEBROW_CLASS,
  DeviceSectionCard,
  DeviceSectionHeader,
  DeviceSwitch,
} from "../modal/deviceModalParts";

interface Props {
  accounts: DeviceAccount[];
  selectedIds: number[];
  onSelectedChange: (ids: number[]) => void;
  // „Всички акаунти": печатат всички свободни, без списък.
  isAllAccounts: boolean;
  onAllAccountsChange: (isAll: boolean) => void;
  // Статусът се показва само при редакция.
  showStatus: boolean;
  isEnabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  isNarrow: boolean;
}

/**
 * Секция „Документи от касата" — кои акаунти печатат бележките си тук.
 * „Неактивно" маха всички акаунти при запазване; станциите (другата секция)
 * не се засягат.
 */
const PosDocumentsSection = ({
  accounts,
  selectedIds,
  onSelectedChange,
  isAllAccounts,
  onAllAccountsChange,
  showStatus,
  isEnabled,
  onEnabledChange,
  isNarrow,
}: Props) => {
  const { t } = useTranslation();
  const selectedSet = new Set(selectedIds);
  const freeAccounts = accounts.filter((account) => account.takenBy === null);
  const chosen = isAllAccounts
    ? freeAccounts
    : freeAccounts.filter((account) => selectedSet.has(account.id));
  const takenAccounts = accounts.filter((account) => account.takenBy !== null);
  const takenPrinters = [...new Set(takenAccounts.map((account) => account.takenBy))];
  const sectionPadding = isNarrow ? "p-4" : "px-4 py-3";

  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<ReceiptIcon size={18} />}
        iconClassName="bg-brand-tint text-brand-deep-ink"
        title={t("devices.posModal.docsTitle")}
        description={t("devices.posModal.docsDesc")}
        shortDescription={t("devices.posModal.docsDescShort")}
        isNarrow={isNarrow}
        action={
          showStatus && (
            <DeviceSwitch
              size="lg"
              checked={isEnabled}
              onChange={onEnabledChange}
              label={isEnabled ? t("devices.posModal.active") : t("devices.posModal.inactive")}
              className="ml-auto"
            />
          )
        }
      />

      {!isEnabled && (
        <div
          className={`flex items-start gap-2.5 rounded-[10px] bg-warn-tint px-3 py-2.5 text-[12.5px] leading-[1.45] text-warn-ink ${
            isNarrow ? "mx-4 mt-3.5" : "mx-4 mt-3"
          }`}
        >
          <WarningIcon size={16} className="mt-px shrink-0" />
          <div>
            <b>{t("devices.posModal.inactiveBannerTitle")}</b>{" "}
            {t("devices.posModal.inactiveBannerText")}
          </div>
        </div>
      )}

      <div
        className={`flex flex-col ${isNarrow ? "gap-3.5" : "gap-3"} ${sectionPadding} ${
          isEnabled ? "" : "pointer-events-none opacity-45"
        }`}
        // Изключено: и клавиатурата не стига до списъка, не само мишката.
        inert={!isEnabled}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className={EYEBROW_CLASS}>{t("devices.posModal.accountsLabel")}</span>
          <span className="rounded-full bg-brand-tint px-[9px] py-[3px] text-[11px] font-bold text-brand-deep-ink">
            {isAllAccounts
              ? t("devices.posModal.allPill", { count: freeAccounts.length })
              : t("devices.posModal.selectedPill", { count: chosen.length })}
          </span>
          {!isNarrow && (
            <DeviceSwitch
              size="sm"
              checked={isAllAccounts}
              onChange={onAllAccountsChange}
              label={t("devices.posModal.allAccounts")}
              className="ml-auto"
            />
          )}
        </div>

        {/* На телефон „Всички акаунти" е самостоятелен ред на цялата ширина. */}
        {isNarrow && (
          <DeviceSwitch
            size="lg"
            checked={isAllAccounts}
            onChange={onAllAccountsChange}
            label={t("devices.posModal.allAccounts")}
            className="w-full justify-between rounded-[12px] border border-shade-3 !px-3.5 !py-2.5 !text-[14px] !font-semibold"
          />
        )}

        {isAllAccounts ? (
          <div className="flex items-center gap-2.5 rounded-[10px] border border-brand-tint2 bg-brand-tint px-3 py-2.5 text-[13px] leading-[1.45] text-ink">
            <CheckIcon size={16} strokeWidth={2.5} className="shrink-0 text-brand-deep-ink" />
            <span>
              <Trans
                i18nKey="devices.posModal.allAccountsNote"
                values={{ count: freeAccounts.length }}
                components={{ b: <b /> }}
              />
            </span>
          </div>
        ) : (
          <DeviceAccountList
            accounts={accounts}
            selectedIds={selectedSet}
            onChange={onSelectedChange}
            isNarrow={isNarrow}
            isDense
          />
        )}

        {takenAccounts.length > 0 && (
          <div className="flex items-start gap-2 rounded-[10px] bg-canvas px-3 py-2 text-[11.5px] leading-[1.4] text-body">
            <LockIcon size={14} className="mt-px shrink-0" />
            <span>
              {t("devices.posModal.takenNote", {
                count: takenAccounts.length,
                where:
                  takenPrinters.length === 1
                    ? `„${takenPrinters[0]}“`
                    : t("devices.posModal.takenWhereMany"),
              })}
            </span>
          </div>
        )}
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(PosDocumentsSection);
